// The library as the engine reads it, built from the same rows the Library screen edits.

import { useMemo } from 'react';
import { LIBRARY_TABLES, buildLibrary, currentVersion, type LibraryRows } from '@zerogap/db';
import type { Library } from '@zerogap/engine';
import { useRows } from './api';

export function useLibraryRows(): { rows: LibraryRows; loading: boolean; error: Error | null } {
  const rows = {} as LibraryRows;
  let loading = false;
  let error: Error | null = null;
  // the table list is a constant, so the hooks run in the same order every render
  for (const t of LIBRARY_TABLES) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const r = useRows(t);
    rows[t] = r.rows;
    loading = loading || r.loading;
    error = error ?? r.error;
  }
  // hand back the same object until one of the tables actually changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useMemo(() => rows, LIBRARY_TABLES.map((t) => rows[t]));
  return { rows: stable, loading, error };
}

export function useLibrary(versionId?: string | null): { lib: Library | null; rows: LibraryRows; loading: boolean; error: Error | null } {
  const { rows, loading, error } = useLibraryRows();
  const lib = useMemo(() => {
    if (loading || rows.standards_version.length === 0) return null;
    try {
      return buildLibrary(rows, versionId);
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, versionId, ...LIBRARY_TABLES.map((t) => rows[t])]);
  return { lib, rows, loading, error };
}

export { currentVersion };
